import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy
} from '@angular/core';

const HIGHLIGHT_CLASS = 'citation-quote-highlight';
const MIN_QUOTE_LENGTH = 16;
const MAX_HIGHLIGHTS = 20;
const TARGET_SELECTOR = [
  '.hb-paragraph',
  '.hb-list li',
  '.cba-table-html th',
  '.cba-table-html td',
  '.cba-table th',
  '.cba-table td'
].join(', ');

interface CharacterLocation {
  node: Text;
  startOffset: number;
  endOffset: number;
}

interface NormalizedText {
  text: string;
  locations: CharacterLocation[];
}

interface HighlightMatch {
  start: number;
  end: number;
}

interface TextSegment {
  node: Text;
  startOffset: number;
  endOffset: number;
}

function normalizeCharacter(character: string): string {
  if (/\s/u.test(character)) return ' ';
  if (character === '\u2018' || character === '\u2019') return "'";
  if (character === '\u201c' || character === '\u201d') return '"';
  if (character === '\u2013' || character === '\u2014' || character === '\u2212') return '-';
  return character.toLowerCase();
}

function normalizeQuote(value: string): string {
  return Array.from(value || '')
    .map(normalizeCharacter)
    .join('')
    .replace(/\s+/gu, ' ')
    .trim();
}

function collectText(root: Element): NormalizedText {
  const documentRef = root.ownerDocument;
  const showText = documentRef.defaultView?.NodeFilter.SHOW_TEXT ?? 4;
  const walker = documentRef.createTreeWalker(root, showText);
  const locations: CharacterLocation[] = [];
  let text = '';
  let node = walker.nextNode() as Text | null;

  while (node) {
    const value = node.nodeValue || '';
    for (let offset = 0; offset < value.length; offset++) {
      const normalized = normalizeCharacter(value[offset]);
      if (normalized === ' ' && text.endsWith(' ')) continue;

      text += normalized;
      locations.push({
        node,
        startOffset: offset,
        endOffset: offset + 1
      });
    }
    node = walker.nextNode() as Text | null;
  }

  return { text, locations };
}

function findMatches(text: string, quotes: readonly string[]): HighlightMatch[] {
  const normalizedQuotes = Array.from(new Set(
    (quotes || [])
      .filter((quote): quote is string => typeof quote === 'string')
      .map(normalizeQuote)
      .filter((quote) => (
        quote.length >= MIN_QUOTE_LENGTH &&
        quote.split(/\s+/u).length >= 3
      ))
  )).sort((left, right) => right.length - left.length);

  const occupied = new Array(text.length).fill(false);
  const matches: HighlightMatch[] = [];

  for (const quote of normalizedQuotes) {
    let searchFrom = 0;
    while (matches.length < MAX_HIGHLIGHTS) {
      const start = text.indexOf(quote, searchFrom);
      if (start < 0) break;

      const end = start + quote.length;
      const overlaps = occupied.slice(start, end).some(Boolean);
      if (!overlaps) {
        occupied.fill(true, start, end);
        matches.push({ start, end });
      }
      searchFrom = start + Math.max(quote.length, 1);
    }

    if (matches.length >= MAX_HIGHLIGHTS) break;
  }

  return matches.sort((left, right) => right.start - left.start);
}

function textSegmentsForMatch(
  locations: CharacterLocation[],
  match: HighlightMatch
): TextSegment[] {
  const segments: TextSegment[] = [];

  for (const location of locations.slice(match.start, match.end)) {
    const previous = segments[segments.length - 1];
    if (previous?.node === location.node) {
      previous.endOffset = location.endOffset;
      continue;
    }

    segments.push({ ...location });
  }

  return segments;
}

export function clearQuoteHighlights(root: HTMLElement): void {
  root.querySelectorAll(`mark.${HIGHLIGHT_CLASS}`).forEach((highlight) => {
    const parent = highlight.parentNode;
    if (!parent) return;

    while (highlight.firstChild) {
      parent.insertBefore(highlight.firstChild, highlight);
    }
    parent.removeChild(highlight);
    parent.normalize();
  });
}

export function applyQuoteHighlights(
  root: HTMLElement,
  quotes: readonly string[] | null | undefined
): number {
  clearQuoteHighlights(root);
  if (!quotes?.length) return 0;

  let highlightCount = 0;
  const targets = Array.from(root.querySelectorAll(TARGET_SELECTOR));

  for (const target of targets) {
    if (highlightCount >= MAX_HIGHLIGHTS) break;

    const normalized = collectText(target);
    if (!normalized.text || !normalized.locations.length) continue;

    const remainingSlots = MAX_HIGHLIGHTS - highlightCount;
    const matches = findMatches(normalized.text, quotes).slice(0, remainingSlots);

    for (const match of matches) {
      const segments = textSegmentsForMatch(normalized.locations, match);
      let segmentCount = 0;

      for (const segment of [...segments].reverse()) {
        const range = root.ownerDocument.createRange();
        range.setStart(segment.node, segment.startOffset);
        range.setEnd(segment.node, segment.endOffset);
        if (range.collapsed) continue;

        const highlight = root.ownerDocument.createElement('mark');
        highlight.className = HIGHLIGHT_CLASS;

        try {
          range.surroundContents(highlight);
          segmentCount++;
        } catch {
          // A missed segment is preferable to changing source markup.
        } finally {
          range.detach();
        }
      }

      if (segmentCount > 0) highlightCount++;
    }
  }

  return highlightCount;
}

export function scrollFirstQuoteHighlightIntoView(root: HTMLElement): boolean {
  const firstHighlight = root.querySelector<HTMLElement>(`mark.${HIGHLIGHT_CLASS}`);
  const scrollContainer = root.closest<HTMLElement>('.modal-content');
  if (!firstHighlight || !scrollContainer) return false;

  const highlightRect = firstHighlight.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();
  const targetTop = (
    scrollContainer.scrollTop +
    highlightRect.top -
    containerRect.top -
    scrollContainer.clientHeight * 0.25
  );

  scrollContainer.scrollTo({
    top: Math.max(0, targetTop),
    behavior: 'auto'
  });
  return true;
}

@Directive({
  selector: '[appQuoteHighlight]'
})
export class QuoteHighlightDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input('appQuoteHighlight') quotedTexts: readonly string[] | null | undefined = [];

  private viewInitialized = false;
  private renderVersion = 0;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.scheduleHighlighting();
  }

  ngOnChanges(): void {
    if (this.viewInitialized) this.scheduleHighlighting();
  }

  ngOnDestroy(): void {
    this.renderVersion++;
  }

  private scheduleHighlighting(): void {
    const version = ++this.renderVersion;
    Promise.resolve().then(() => {
      if (version !== this.renderVersion) return;
      const root = this.elementRef.nativeElement;
      const highlightCount = applyQuoteHighlights(root, this.quotedTexts);
      if (highlightCount > 0) scrollFirstQuoteHighlightIntoView(root);
    });
  }
}
